import {CLASS_SEAT_WITH_RESTRICTIONS} from './amadeus-seatmap-service';

import {
    SEAT_SUITABLE_FOR_UNUSAL_NEEDS,
    SEAT_SPECIFIC_RESTRICTED,
    SEAT_ENTERTAINMENT,
    SEAT_ENTERTAINMENT_LABEL,
    SEAT_SURROUNDING,
    SEAT_SURROUNDING_LABEL,
    SEAT_GENERAL_RESTRICTED,
    SEAT_CONVENIENCE,
    SEAT_POSITION,
    SEAT_POSITION_LIST,
    SEAT_POSITION_LABEL,
    SEAT_CONVENIENCE_LABEL,
    SEAT_CONVENIENCE_LIST,
    SEAT_SUITABLE_FOR_UNUSAL_NEEDS_LABEL,
    SEAT_ENTERTAINMENT_LIST,
    SEAT_SURROUNDING_LIST,
    SEAT_SUITABLE_FOR_UNUSAL_NEEDS_LIST,
    SEAT_GENERAL_RESTRICTED_LIST,
} from './amadeus-seat-enum';

export {
    SEAT_SUITABLE_FOR_UNUSAL_NEEDS,
    SEAT_SPECIFIC_RESTRICTED,
    SEAT_ENTERTAINMENT,
    SEAT_ENTERTAINMENT_LABEL,
    SEAT_SURROUNDING,
    SEAT_SURROUNDING_LABEL,
    SEAT_GENERAL_RESTRICTED,
    SEAT_CONVENIENCE,
    SEAT_POSITION,
    SEAT_POSITION_LIST,
    SEAT_POSITION_LABEL,
    SEAT_CONVENIENCE_LABEL,
    SEAT_CONVENIENCE_LIST,
};

export const SEAT_TOOLTIP_FEATURE_LIST = [
    ...SEAT_SUITABLE_FOR_UNUSAL_NEEDS_LIST,
    ...SEAT_CONVENIENCE_LIST,
    ...SEAT_ENTERTAINMENT_LIST,
    ...SEAT_SURROUNDING_LIST,
];

export const SEAT_TOOLTIP_NOT_ALLOWED_LIST = [
    ...SEAT_GENERAL_RESTRICTED_LIST,
    SEAT_SPECIFIC_RESTRICTED.SEAT_NOT_ALLOWED_FOR_UNACCOMPANIED_MINOR,
    SEAT_SPECIFIC_RESTRICTED.SEAT_TO_BE_LEFT_VACANT_OR_OFFERED_LAST,
    SEAT_SPECIFIC_RESTRICTED.DEPORTEE,
    SEAT_SPECIFIC_RESTRICTED.CREW_SEAT,
];

export const SEAT_TOOLTIP_FEATURE_LABEL = {
    ...SEAT_SUITABLE_FOR_UNUSAL_NEEDS_LABEL,
    ...SEAT_CONVENIENCE_LABEL,
    ...SEAT_ENTERTAINMENT_LABEL,
    ...SEAT_SURROUNDING_LABEL,
};

export const SEAT_PRICE_FREE = 'free';
export const PREMIUM_PRICED_SEAT = 'premium priced seat by airline';

export default function AmadeusSeatService(
    userService,
    clubCardService,
) {
    'ngInject';

    Object.assign(this, {
        isSeatWithFeatures,
        getSeatDetailedDescription,
        isSeatAvailableForSelection,
        isSeatOccupied,
        isSeatAvailableForTravellerWithInfantForSelection,
        isSeatSuitableForPassenger,
        setTextForTooltipSeat,
        doesSeatHaveRestrictions,
        getTravellerForWhomWeAreCurrentlySelectingASeat,
        getSeatPositionLabels,
        getSeatFeatureLabels,
        getSeatNameAndPositionLabels,
        getTier,
        restrictSeatSelection,
        unRestrictSeatSelection,
    });

    function isSeatWithFeatures(seat) {
        return seat.data.characteristics.some((feature) =>
            SEAT_TOOLTIP_FEATURE_LIST.includes(feature));
    }

    function isSeatAvailableForSelection(seat) {
        return !!(seat
            && seat.data
            && seat.data.selectable);
    }

    function isSeatOccupied(seat) {
        return !!(seat
            && seat.data
            && seat.data.occupied);
    }

    function isSeatSuitableForPassenger(seat, map) {
        return map.isSuitableForPassenger(seat);
    }

    function isSeatAvailableForTravellerWithInfantForSelection(seat, map, isAllSeatAllowedByDefaultForTravellerWithInfant) {
        if (!map.isSuitableForPassenger(seat)) {
            return false;
        }

        const seatHasCharacteristics = seat
            && seat.data
            && seat.data.characteristics;

        if (!seatHasCharacteristics) {
            return false;
        }

        return isAllSeatAllowedByDefaultForTravellerWithInfant
            ? !isTheSeatNotSuitableForAdultWithInfant()
            : isTheSeatSuitableForAdultWithInfant();

        function isTheSeatSuitableForAdultWithInfant() {
            return seat.data.characteristics
                .includes(SEAT_SUITABLE_FOR_UNUSAL_NEEDS.SUITABLE_FOR_ADULT_WITH_INFANT);
        }

        function isTheSeatNotSuitableForAdultWithInfant() {
            return seat.data.characteristics
                .includes(SEAT_SPECIFIC_RESTRICTED.SEAT_NOT_ALLOWED_FOR_INFANT);
        }
    }

    function setTextForTooltipSeat(tooltip, text) {
        tooltip.innerHTML = text;
    }

    function getSeatDetailedDescription(seat, flight) {
        const currencySign = userService.getCurrencySign();
        const seatPrice = seat.getPrice();
        const price = seatPrice ? `${currencySign}${seatPrice}` : SEAT_PRICE_FREE;
        const nameAndPosition = this.getSeatNameAndPositionLabels(seat);

        const features = this.getSeatFeatureLabels(seat, flight)
            .map((label) => `<li>${label}</li>`)
            .join('');

        return `
            <div class="tooltip-seat-price">${price}</div>
            <div class="tooltip-seat-name">${nameAndPosition}</div>
            <ul class="tooltip-seat-features">${features}</ul>
        `;
    }

    function getSeatNameAndPositionLabels(seat) {
        return [
            seat.name,
            ...getSeatPositionLabels(seat),
        ].join(', ');
    }

    function getSeatPositionLabels(seat) {
        return seat.data.characteristics
            .filter((feature) => SEAT_POSITION_LIST.includes(feature))
            .map((position) => SEAT_POSITION_LABEL[position]);
    }

    function getSeatFeatureLabels(seat, flight) {
        const isCabinClassEconomy = flight.CabinClass === 'Economy';
        const labels = seat.data.characteristics
            .filter((feature) => SEAT_TOOLTIP_FEATURE_LIST.includes(feature))
            .map((feature) => mapFeatureToLabels(feature, isCabinClassEconomy));

        const shouldAddPremiumPricedSeatLabel = isCabinClassEconomy
            && !!seat.getPrice()
            && !labels.includes(PREMIUM_PRICED_SEAT);

        if (shouldAddPremiumPricedSeatLabel) {
            return [PREMIUM_PRICED_SEAT, ...labels];
        }

        return labels;
    }

    function mapFeatureToLabels(feature, isEconomyClass) {
        if (isEconomyClass && feature === SEAT_CONVENIENCE.PREMIUM_SEAT) {
            return PREMIUM_PRICED_SEAT;
        }
        return SEAT_TOOLTIP_FEATURE_LABEL[feature];
    }

    function doesSeatHaveRestrictions(seat) {
        return !!seat.restricted
            || seat.data.characteristics.some((characteristic) =>
            SEAT_TOOLTIP_NOT_ALLOWED_LIST.includes(characteristic));
    }

    function getTravellerForWhomWeAreCurrentlySelectingASeat(map, travellers) {
        const passengers = map.passengers();
        const selectedPassengerIndex = map.selected.passenger;
        const selectedPassenger = passengers[selectedPassengerIndex];

        if (!selectedPassenger) {
            return null;
        }

        return travellers.find(({FirstName, LastName}) =>
            selectedPassenger.firstName === FirstName && selectedPassenger.surname === LastName)
            || null;
    }

    function getTier(flight, selectedSeat, tierList) {
        if (!(flight && flight.Id && selectedSeat && selectedSeat.TravellerClubCardId)) {
            return '';
        }
        const flightId = flight.Id;
        const clubCardId = selectedSeat.TravellerClubCardId;
        return clubCardService.getTier(clubCardId, flightId, tierList);
    }

    function restrictSeatSelection(seat, seatElement) {
        seat.isRestricted = true;
        seatElement.classList.add(CLASS_SEAT_WITH_RESTRICTIONS);
    }

    function unRestrictSeatSelection(seat, seatElement) {
        seat.isRestricted = false;
        seatElement.classList.remove(CLASS_SEAT_WITH_RESTRICTIONS);
    }
}



// WEBPACK FOOTER //
// ./src/app/modules/flights/flight-seatmap/amadeus-seatmap/amadeus-seat-service.js